import * as THREE from 'three';
import { ARCHETYPE_INDEX, buildAllMeshes } from './art/models';
import { buildEnvironment } from './Environment';
import { buildPaletteAtlas, makeHullMaterial } from './materials/hullMaterial';

/* ═══════════════════════════════════════════════════════════════════════════
   L'APERÇU 3D DU HANGAR — un renderer AUTONOME, hors de la boucle de jeu.

   Pendant les menus, le canvas de jeu (`Stage`) est masqué et la simulation est
   à l'arrêt : l'aperçu ne peut donc pas s'y greffer. C'est une petite scène
   INDÉPENDANTE — son propre `WebGLRenderer`, sa propre caméra, ses propres
   lumières — montée dans l'écran Hangar et démontée avec lui.

   ── Deux règles qui gouvernent tout le fichier ────────────────────────────

   1. **Aucune fuite WebGL.** Un menu qu'on ouvre et referme cent fois ne doit
      pas accumuler cent contextes GL. Tout ce qui alloue du GPU — géométries,
      matériaux, textures, le renderer lui-même — est pisté dans `disposables`
      et rendu à `dispose()`, qui coupe aussi la boucle d'animation et force la
      perte du contexte. C'est la contrepartie du choix « renderer séparé ».

   2. **La dégradation ne crashe jamais.** Sur un navigateur sans WebGL, la
      création du contexte échoue ; on l'attrape, on lève `ok = false`, et
      l'appelant masque simplement l'aperçu. L'écran Hangar reste utilisable —
      la méta-progression n'a pas besoin d'un GPU pour se cliquer.

   ── Ce que l'aperçu REFLÈTE ────────────────────────────────────────────────

   Le catalogue ne contient qu'UNE silhouette joueur (l'archétype `player` de
   `models.ts`) : tous les vaisseaux la partagent. Faute de variante de MESH,
   l'apparence varie par la LUMIÈRE — un accent coloré par arme principale, une
   teinte émissive sur la coque, une lumière d'appoint par arme secondaire, et
   une aura qui s'avive avec le nombre d'améliorations possédées. Les variantes
   de SILHOUETTE par vaisseau sont une évolution future, documentée ici pour
   qu'on sache où la brancher (une géométrie par `shipId` dans `models.ts`).
   ═══════════════════════════════════════════════════════════════════════════ */

/** L'équipement à refléter dans l'aperçu. Les identifiants sont ceux des
 *  catalogues `defs.ships` / `defs.weapons` / secondaires ; les noms sont
 *  facultatifs et ne servent qu'à la légende. */
export interface HangarLoadout {
  readonly shipId: string;
  readonly weaponId: string;
  readonly secondaryId: string;
  /** Identifiants des nœuds méta possédés (`saves.data.unlocked`). Sert à faire
   *  varier l'intensité de l'accent — plus on a débloqué, plus l'aura brille. */
  readonly upgrades: readonly string[];
  // `| undefined` explicite : le projet active `exactOptionalPropertyTypes`, et
  // l'appelant passe le résultat d'un `.find(...)?.name` (donc `string | undefined`).
  readonly shipName?: string | undefined;
  readonly weaponName?: string | undefined;
}

/** FOV de présentation, plus ouvert que les 30° du jeu : on montre un objet, on
 *  ne cadre pas un terrain, et un peu de perspective donne du volume. */
const FOV = 34;

/** Rotation lente de présentation, en radians/seconde. Assez lente pour lire la
 *  silhouette, assez vive pour que le vaisseau ait l'air « présenté ». */
const ROT_SPEED = 0.6;

/** Inclinaison fixe de la présentation : on bascule la proue vers la caméra pour
 *  passer d'une vue de face (plate) à un trois-quarts plongeant qui révèle les
 *  facettes de la carène. */
const TILT_X = 0.34;

/** Accent NÉON par arme principale. Le catalogue en compte trois ; toute arme
 *  inconnue reçoit une teinte dérivée de son identifiant (voir `safeAccent`).
 *
 *  ⚠️ Toutes ces teintes sont HORS de la bande réservée rouge/rose/violet
 *  (`palette.ts`) : même dans un aperçu de menu, une surface 3D ne doit jamais
 *  emprunter la couleur de ce qui tue en jeu. */
const WEAPON_ACCENT: Readonly<Record<string, number>> = {
  vulcan: 0x3fe8ff, // cyan — le canon de série
  plasma: 0x54ffb0, // vert — la salve lourde
  faisceau: 0xb8f4ff, // cyan pâle — le faisceau focalisé
};

/** Teinte d'appoint par arme secondaire. Non exhaustif : le reste passe par
 *  `safeAccent`. */
const SECONDARY_ACCENT: Readonly<Record<string, number>> = {
  'lance-perforante': 0x54ffb0,
  mines: 0xffc93c,
  flanqueurs: 0x7fd9f2,
};

const DEFAULT_ACCENT = 0x3fe8ff;

/** Teinte NÉON déterministe pour un identifiant hors table.
 *
 *  La teinte est bornée à [40°, 200°] — ambre → vert → cyan → bleu — ce qui
 *  couvre toute la plage « sûre » et évite par construction la bande réservée
 *  (262° → 14° en passant par le rouge). Un identifiant ajouté demain reçoit
 *  donc une couleur cohérente sans qu'on ait à maintenir une table. */
function safeAccent(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const hue = 40 + (h % 161);
  return hsvToHex(hue, 0.72, 1);
}

/** HSV → entier 0xRRGGBB. Local au fichier : on n'a besoin que d'un sens de
 *  conversion, et l'atlas du jeu travaille déjà en hexadécimal. */
function hsvToHex(h: number, s: number, v: number): number {
  const c = v * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp < 1) [r, g] = [c, x];
  else if (hp < 2) [r, g] = [x, c];
  else if (hp < 3) [g, b] = [c, x];
  else if (hp < 4) [g, b] = [x, c];
  else if (hp < 5) [r, b] = [x, c];
  else [r, b] = [c, x];
  const m = v - c;
  const to = (n: number): number => Math.max(0, Math.min(255, Math.round((n + m) * 255)));
  return (to(r) << 16) | (to(g) << 8) | to(b);
}

export class HangarPreview {
  private _ok = false;
  private renderer: THREE.WebGLRenderer | null = null;
  private readonly scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera | null = null;
  /** Le groupe qui TOURNE — le vaisseau seul. L'anneau et l'inclinaison vivent
   *  au-dessus, pour que l'aura reste face à la caméra pendant que la coque
   *  pivote à l'intérieur. */
  private spin: THREE.Group | null = null;
  private hullMat: THREE.MeshStandardMaterial | null = null;
  private ringMat: THREE.MeshBasicMaterial | null = null;
  /** La coque affichée et l'identifiant du vaisseau qu'elle représente. On ne
   *  reconstruit sa géométrie QUE lorsqu'il change — `setLoadout` est appelé à
   *  chaque rendu, une reconstruction par frame allouerait sans fin. */
  private ship: THREE.Mesh | null = null;
  private shownShip = '';
  /** Lumière d'appoint colorée par l'arme secondaire. */
  private fill: THREE.DirectionalLight | null = null;
  private atlas: THREE.Texture | null = null;
  private env: THREE.Texture | null = null;

  /** Tout ce qui détient du GPU, pisté pour un démontage sans fuite. */
  private readonly disposables: { dispose(): void }[] = [];
  private raf = 0;
  private last = 0;
  private ro: ResizeObserver | null = null;
  private readonly canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    /* Le renderer est créé EN PREMIER, et c'est le seul point qui peut échouer
       sur un navigateur pauvre : `three` lève si aucun contexte WebGL n'est
       disponible. On attrape ici plutôt que de laisser remonter, pour que
       `new HangarPreview()` soit toujours sûr et que l'appelant n'ait qu'à lire
       `ok`. Rien d'autre n'est alloué avant ce point : un échec ne laisse donc
       rien à nettoyer. */
    try {
      this.renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true, // le panneau du hangar transparaît derrière la scène
        powerPreference: 'low-power', // un aperçu de menu n'est pas un jeu
      });
    } catch {
      this._ok = false;
      return;
    }

    const r = this.renderer;
    r.setClearColor(0x000000, 0);
    r.outputColorSpace = THREE.SRGBColorSpace;
    /* Contrairement au `Stage` du jeu — qui DOIT rester en `NoToneMapping` à
       cause de sa chaîne de post-processing — l'aperçu n'a pas de composer. On
       s'autorise donc un tonemapping ACES : les accents émissifs dépassent 1,0
       (gain de bloom 3,6 dans le matériau), et sans bloom ils écrêteraient en
       aplat. ACES leur donne un joli rolloff au lieu d'un blanc plat. */
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.toneMappingExposure = 1.1;

    // La même PMREM néon que le jeu : sans elle, le métal de la coque est plat.
    this.env = buildEnvironment(r, this.scene);

    this.camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 100);

    /* ── L'éclairage, calqué sur celui du jeu ──────────────────────────────
       Créé une fois et jamais retiré : ici le nombre de lumières importe peu
       (renderer isolé), mais on garde la même facture que `Stage` pour que le
       vaisseau ait exactement l'allure qu'il aura en jeu. */
    this.scene.add(new THREE.AmbientLight(0x8899bb, 1.4));
    const key = new THREE.DirectionalLight(0xffffff, 1.7);
    key.position.set(-0.4, 0.8, 1);
    this.scene.add(key);
    this.fill = new THREE.DirectionalLight(DEFAULT_ACCENT, 0.9);
    this.fill.position.set(0.6, -0.5, 0.4);
    this.scene.add(this.fill);

    // ── La coque : l'archétype `player`, peint par le matériau unique du jeu ──
    this.atlas = buildPaletteAtlas();
    this.disposables.push(this.atlas);
    this.hullMat = makeHullMaterial(this.atlas);
    this.disposables.push(this.hullMat);

    // Silhouette de SÉRIE (VULCAN) au montage ; `setLoadout` la remplace par la
    // coque réellement sélectionnée. La géométrie n'est PAS pistée dans
    // `disposables` — elle est gérée à part (libérée au remplacement et au
    // démontage), sinon un échange la libérerait deux fois.
    const geo = this.shipGeo('vulcan');
    this.shownShip = 'vulcan';
    const radius = geo.boundingSphere?.radius ?? 1;

    // Inclinaison fixe (parent) → rotation (enfant) : la coque pivote sur son axe
    // vertical, l'ensemble reste incliné. L'anneau va dans le parent incliné,
    // donc il ne tourne pas avec la coque.
    const tilt = new THREE.Group();
    tilt.rotation.x = TILT_X;
    this.scene.add(tilt);
    this.spin = new THREE.Group();
    tilt.add(this.spin);

    const ship = new THREE.Mesh(geo, this.hullMat);
    this.ship = ship;
    this.spin.add(ship);

    // ── L'anneau d'énergie : la SIGNATURE de l'arme principale ──────────────
    // `MeshBasicMaterial` additif : non éclairé, toujours vif, il brille sur le
    // fond sombre comme un néon même sans passe de bloom.
    const ringGeo = new THREE.TorusGeometry(radius * 1.12, radius * 0.025, 8, 48);
    this.disposables.push(ringGeo);
    this.ringMat = new THREE.MeshBasicMaterial({
      color: DEFAULT_ACCENT,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.disposables.push(this.ringMat);
    const ring = new THREE.Mesh(ringGeo, this.ringMat);
    ring.position.z = -radius * 0.1;
    tilt.add(ring);

    // Cadrage : on recule jusqu'à ce que l'anneau tienne dans le champ vertical.
    const frame = radius * 1.35;
    const dist = frame / Math.tan((FOV * Math.PI) / 360);
    this.camera.position.set(0, 0, dist * 1.12);
    this.camera.lookAt(0, 0, 0);

    canvas.addEventListener('webglcontextlost', this.onContextLost, false);
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(canvas);
    }
    this.resize();

    this._ok = true;
  }

  /** `true` si le contexte WebGL est bien en place. `false` = aperçu dégradé,
   *  l'appelant doit masquer le canvas. */
  get ok(): boolean {
    return this._ok;
  }

  /** Géométrie CENTRÉE de la coque d'un vaisseau. Retombe sur la silhouette de
   *  série (`player`) pour tout identifiant sans archétype `ship-<id>` dédié — la
   *  même sûreté que côté jeu (`View.setShip`). Alloue : réservé aux changements
   *  de vaisseau, jamais par frame. */
  private shipGeo(shipId: string): THREE.BufferGeometry {
    const meshes = buildAllMeshes();
    const archId = ARCHETYPE_INDEX.has(`ship-${shipId}`) ? `ship-${shipId}` : 'player';
    const data = meshes[ARCHETYPE_INDEX.get(archId) ?? 0]!;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(data.position, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(data.normal, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(data.uv, 2));
    // Centrée pour que la rotation de présentation tourne autour du vaisseau.
    geo.center();
    geo.computeBoundingSphere();
    return geo;
  }

  /** Reflète l'équipement sélectionné. Sûr à appeler même en mode dégradé. */
  setLoadout(l: HangarLoadout): void {
    if (!this._ok || !this.hullMat || !this.ringMat || !this.fill) return;

    // La SILHOUETTE suit le vaisseau — mais on ne reconstruit qu'au changement.
    if (l.shipId !== this.shownShip && this.ship) {
      const previous = this.ship.geometry;
      this.ship.geometry = this.shipGeo(l.shipId);
      previous.dispose();
      this.shownShip = l.shipId;
    }

    const weapon = WEAPON_ACCENT[l.weaponId] ?? safeAccent(l.weaponId);
    const secondary = SECONDARY_ACCENT[l.secondaryId] ?? safeAccent(l.secondaryId);

    // L'anneau porte l'arme principale.
    this.ringMat.color.setHex(weapon);
    // Le fuselage en capte une TEINTE ÉMISSIVE : discrète, mais changer d'arme
    // change vraiment l'apparence de la coque, pas seulement un décor à côté.
    this.hullMat.emissive.setHex(weapon);
    // Les améliorations possédées AVIVENT l'aura : plus de nœuds → plus d'éclat.
    const upg = l.upgrades.length;
    this.hullMat.emissiveIntensity = 0.12 + Math.min(upg, 8) * 0.02;
    this.ringMat.opacity = Math.min(0.9 + upg * 0.01, 1);
    // L'appoint porte l'arme secondaire, pour qu'elle se distingue aussi.
    this.fill.color.setHex(secondary);
  }

  /** Démarre la boucle de présentation. Idempotent. */
  start(): void {
    if (!this._ok || this.raf !== 0) return;
    this.last = now();
    const tick = (): void => {
      this.raf = requestAnimationFrame(tick);
      const t = now();
      // Borné : un onglet en arrière-plan peut rendre `dt` énorme, ce qui ferait
      // sauter la rotation d'un tour au réveil.
      const dt = Math.min((t - this.last) / 1000, 0.05);
      this.last = t;
      if (this.spin) this.spin.rotation.y += dt * ROT_SPEED;
      this.renderer?.render(this.scene, this.camera!);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Arrête la boucle. Idempotent. */
  stop(): void {
    if (this.raf !== 0) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  }

  /**
   * Démonte tout et rend le GPU. Après `dispose()`, l'objet est inerte.
   *
   * C'est la preuve d'absence de fuite : la boucle est coupée, l'observateur de
   * taille déconnecté, chaque géométrie / matériau / texture pistée est libérée,
   * puis le contexte est explicitement perdu (`forceContextLoss`) pour que le
   * navigateur récupère la mémoire vidéo sans attendre le ramasse-miettes.
   */
  dispose(): void {
    this.stop();
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost, false);
    this.ro?.disconnect();
    this.ro = null;

    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    // La géométrie de la coque est gérée hors `disposables` (elle est remplacée
    // au changement de vaisseau) : on la libère explicitement ici.
    this.ship?.geometry.dispose();
    this.ship = null;
    this.env?.dispose();
    this.env = null;

    if (this.renderer) {
      this.renderer.dispose();
      // Libère le contexte GL immédiatement plutôt qu'à la prochaine GC.
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
    this._ok = false;
  }

  private resize(): void {
    if (!this.renderer || !this.camera) return;
    const w = this.canvas.clientWidth || 320;
    const h = this.canvas.clientHeight || 360;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    // `false` : la TAILLE CSS du canvas est pilotée par la feuille de style, on
    // ne fixe que le tampon de rendu.
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  /** Contexte perdu (onglet en veille, driver qui recycle) : on coupe la boucle
   *  pour ne pas rendre dans un contexte mort. */
  private readonly onContextLost = (e: Event): void => {
    e.preventDefault();
    this.stop();
  };
}

/** `performance.now()` quand il existe, `Date.now()` sinon. L'aperçu doit tolérer
 *  un environnement minimal. */
function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
